/**
 * MobileMoreSheet — overflow destinations for the floating nav.
 *
 * Primary slots in FloatingNav are Home / Library / Brand + the
 * centre-FAB; everything else (Studio, Editor, Calendar, Team,
 * Settings, Plans, Inspiration, Admin) lives here.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sparkles, Brush, Calendar as CalendarIcon, Users, Layers,
  Settings, Heart, Crown, Shield, LogOut, X, Moon, Sun,
} from "lucide-react";

import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { ROUTES, type AppRoute } from "@/types";
import { initials } from "@/utils/format";
import { optimizedUrl, optimizedSrcSet } from "@/lib/files";

interface Props {
  onClose: () => void;
  onLogout: () => void;
}

type Item = {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  label: string;
  to: AppRoute;
  accent?: boolean;
};

const MobileMoreSheet: React.FC<Props> = ({ onClose, onLogout }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const baseItems: Item[] = [
    { icon: Sparkles,     label: "Studio",      to: ROUTES.STUDIO },
    { icon: Brush,        label: "Editor",      to: ROUTES.EDITOR },
    { icon: CalendarIcon, label: "Calendar",    to: ROUTES.CALENDAR },
    { icon: Users,        label: "Team",        to: ROUTES.TEAM },
    { icon: Heart,        label: "Inspiration", to: ROUTES.INSPIRATION },
    { icon: Crown,        label: "Plans",       to: ROUTES.PLANS },
    { icon: Settings,     label: "Settings",    to: ROUTES.SETTINGS, accent: true },
  ];

  const adminItems: Item[] = user?.isSystemAdmin
    ? [
        { icon: Layers, label: "Templates", to: ROUTES.TEMPLATES },
        { icon: Shield, label: "Admin",     to: ROUTES.ADMIN },
      ]
    : [];

  const go = (to: AppRoute) => { onClose(); setTimeout(() => navigate(to), 80); };
  const isActive = (to: string) =>
    pathname === to || (to !== ROUTES.DASHBOARD && pathname.startsWith(to));

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 70,
          background: "rgba(0,0,0,0.42)",
          backdropFilter: "blur(4px)",
          opacity: mounted ? 1 : 0,
          transition: "opacity 240ms",
        }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="More"
        style={{
          position: "fixed",
          left: 0, right: 0, bottom: 0,
          zIndex: 71,
          background: "var(--bg-surface-raised)",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: "8px 12px calc(env(safe-area-inset-bottom, 0px) + 16px)",
          boxShadow: "0 -20px 40px -12px rgba(0,0,0,0.25)",
          maxHeight: "min(80vh, 720px)",
          overflowY: "auto",
          transform: mounted ? "translateY(0)" : "translateY(100%)",
          transition: "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <div style={{
          width: 44, height: 4,
          background: "var(--border-strong, var(--border-default))",
          borderRadius: 999,
          margin: "8px auto 14px",
        }} />

        {/* Profile header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "8px 8px 14px",
          borderBottom: "1px solid var(--border-default)",
          marginBottom: 12,
        }}>
          <div style={{
            width: 48, height: 48,
            borderRadius: "50%",
            background: user?.avatar ? "transparent" : "var(--brand-gradient)",
            overflow: "hidden",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff",
            fontSize: 16, fontWeight: 700,
            fontFamily: "var(--font-display)",
            flexShrink: 0,
          }}>
            {user?.avatar ? (
              <img
                src={optimizedUrl(user, user.avatar, { w: 96, q: 85 })}
                srcSet={optimizedSrcSet(user, user.avatar, 48, { q: 85 })}
                alt=""
                decoding="async"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              initials(user?.displayName || user?.name || user?.email || "?")
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: "var(--font-size-md)",
              fontWeight: 700,
              color: "var(--text-primary)",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {user?.displayName || user?.name || "Creator"}
            </div>
            <div style={{
              fontSize: "var(--font-size-xs)",
              color: "var(--text-tertiary)",
              marginTop: 2,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {user?.email}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36,
              borderRadius: "50%",
              background: "var(--bg-surface-alt)",
              border: "1px solid var(--border-default)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-secondary)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>

        {/* Grid of items */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
        }}>
          {baseItems.map((item) => (
            <SheetTile
              key={item.to}
              item={item}
              active={isActive(item.to)}
              onClick={() => go(item.to)}
            />
          ))}
          {adminItems.length > 0 && (
            <>
              <div style={{
                gridColumn: "1 / -1",
                padding: "8px 4px 0",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "var(--tracking-label)",
                textTransform: "uppercase",
                color: "var(--text-tertiary)",
              }}>
                Admin
              </div>
              {adminItems.map((item) => (
                <SheetTile
                  key={item.to}
                  item={item}
                  active={isActive(item.to)}
                  onClick={() => go(item.to)}
                />
              ))}
            </>
          )}
        </div>

        {/* Footer: theme + sign out */}
        <div style={{
          display: "flex", gap: 8,
          marginTop: 14,
          paddingTop: 14,
          borderTop: "1px solid var(--border-default)",
        }}>
          <button
            onClick={() => toggleTheme()}
            style={{
              flex: 1, height: 48,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: "var(--bg-surface-alt)",
              border: "1px solid var(--border-default)",
              borderRadius: 14,
              color: "var(--text-primary)",
              fontSize: "var(--font-size-sm)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {theme === "light" ? <Moon size={14} strokeWidth={1.75} /> : <Sun size={14} strokeWidth={1.75} />}
            {theme === "light" ? "Dark mode" : "Light mode"}
          </button>
          <button
            onClick={onLogout}
            style={{
              flex: 1, height: 48,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: "var(--status-danger-bg, #fdecec)",
              border: "1px solid var(--status-danger, #e04747)",
              borderRadius: 14,
              color: "var(--status-danger, #e04747)",
              fontSize: "var(--font-size-sm)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <LogOut size={14} strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      </div>
    </>
  );
};

const SheetTile: React.FC<{
  item: Item;
  active: boolean;
  onClick: () => void;
}> = ({ item, active, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "18px 10px",
        minHeight: 92,
        borderRadius: 16,
        background: active
          ? "color-mix(in srgb, var(--accent-300) 16%, var(--bg-surface))"
          : "var(--bg-surface)",
        border: active
          ? "1px solid color-mix(in srgb, var(--accent-300) 40%, transparent)"
          : "1px solid var(--border-default)",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        fontSize: "var(--font-size-xs)",
        fontWeight: active ? 700 : 600,
        cursor: "pointer",
        transition: "var(--transition-fast)",
      }}
    >
      <Icon size={20} strokeWidth={active ? 2 : 1.5} />
      <span>{item.label}</span>
    </button>
  );
};

export default MobileMoreSheet;
